import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {Link, useNavigate} from "react-router-dom";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import {useAuth} from "../context/AuthContext";
import {BRAND, useColorMode} from "../context/ColorModeContext";
import BitlabLogo from "./BitlabLogo";
import LanguageSwitcher from "./LanguageSwitcher";

type Props = {
    onOpenMenu?: () => void;
};

const Navbar = ({onOpenMenu}: Props) => {
    const {t} = useTranslation();
    const {mode, toggleColorMode} = useColorMode();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const {user, isLoading, logout} = useAuth();

    const initials = useMemo(() => {
        const name = (user as any)?.fullName || (user as any)?.name || "";
        if (!name) return "BA";
        const parts = String(name).trim().split(/\s+/);
        const a = parts[0]?.[0] ?? "B";
        const b = parts[1]?.[0] ?? "A";
        return (a + b).toUpperCase();
    }, [user]);

    const handleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleLogout = () => {
        handleClose();
        logout();
        navigate("/login", {replace: true});
    };

    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={(theme) => ({
                background: theme.palette.mode === "dark"
                    ? `linear-gradient(90deg, ${BRAND.headerDark} 0%, ${BRAND.headerDarkEnd} 100%)`
                    : `linear-gradient(90deg, ${BRAND.header} 0%, ${BRAND.headerEnd} 100%)`,
                color: "#ffffff",
                border: "none",
                borderBottom: `1px solid ${alpha(BRAND.accent, theme.palette.mode === "dark" ? 0.2 : 0.25)}`,
                borderRadius: 0,
            })}
        >
            <Toolbar sx={{minHeight: 64, display: "flex", justifyContent: "space-between"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                    <IconButton
                        onClick={onOpenMenu}
                        sx={{
                            display: {xs: "inline-flex", md: "none"},
                            color: "rgba(255,255,255,0.8)",
                        }}
                    >
                        <MenuIcon/>
                    </IconButton>

                    <Box
                        component={Link}
                        to="/"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            textDecoration: "none",
                            color: "inherit",
                        }}
                    >
                        <BitlabLogo size={34}/>
                        <Box sx={{lineHeight: 1}}>
                            <Typography sx={{fontWeight: 800, fontSize: 15, letterSpacing: "-0.2px"}}>
                                BITLAB ACADEMY
                            </Typography>
                            <Typography variant="caption" sx={{color: "rgba(255,255,255,0.6)"}}>
                                {t("nav.lmsSystem")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <LanguageSwitcher/>

                    <Tooltip title={mode === "light" ? t("theme.darkMode") : t("theme.lightMode")}>
                        <IconButton
                            onClick={toggleColorMode}
                            sx={{color: "rgba(255,255,255,0.8)", "&:hover": {color: "#ffffff"}}}
                        >
                            {mode === "light" ? <DarkModeOutlinedIcon/> : <LightModeOutlinedIcon/>}
                        </IconButton>
                    </Tooltip>

                    {!isLoading && user ? (
                        <>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{mx: 0.5, my: 1.5, borderColor: "rgba(255,255,255,0.14)"}}
                            />

                            <Tooltip title={t("nav.account")}>
                                <IconButton onClick={handleMenu} sx={{p: 0.5, borderRadius: "50%"}}>
                                    <Avatar
                                        sx={{
                                            width: 34,
                                            height: 34,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            bgcolor: BRAND.accent,
                                            color: BRAND.header,
                                        }}
                                    >
                                        {initials}
                                    </Avatar>
                                </IconButton>
                            </Tooltip>

                            <Menu
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}
                                slotProps={{paper: {sx: {mt: 1, minWidth: 220}}}}
                            >
                                <Box sx={{px: 2, py: 1.25}}>
                                    <Typography variant="subtitle2" noWrap>
                                        {(user as any)?.fullName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                                        {(user as any)?.email}
                                    </Typography>
                                </Box>
                                <Divider/>
                                <MenuItem component={Link} to="/profile" onClick={handleClose}>
                                    <ListItemIcon><PersonOutlineIcon fontSize="small"/></ListItemIcon>
                                    {t("nav.profile")}
                                </MenuItem>
                                <MenuItem onClick={handleLogout} sx={{color: "error.main"}}>
                                    <ListItemIcon sx={{color: "inherit"}}><LogoutIcon fontSize="small"/></ListItemIcon>
                                    {t("nav.logout")}
                                </MenuItem>
                            </Menu>
                        </>
                    ) : null}
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;